import { useEffect, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Copy, Check, Users, Gift, Share2, Sparkles, Wallet, MessageSquare } from "lucide-react";
import { toast } from "sonner";

export default function Referrals() {
    const { user } = useAuth();
    const { sub } = useOutletContext();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        api.get("/referral/me").then((r) => setData(r.data)).finally(() => setLoading(false));
    }, []);

    const link = data?.code ? `${window.location.origin}/login?ref=${data.code}` : "";
    const credit = ((data?.credit_cents ?? user?.account_credit_cents ?? 0) / 100).toFixed(2);
    const referrerReward = ((data?.referrer_reward_cents ?? data?.reward_per_friend_cents ?? 2500) / 100).toFixed(0);
    const refereeReward = ((data?.referee_reward_cents ?? 5500) / 100).toFixed(0);

    const shareMessage = data?.code
        ? `Girl — you HAVE to try 24K Glow On The Go 💅✨ Luxury mobile nails delivered to your door. Use my code ${data.code} and your first Glow set is on the house ($${refereeReward} credit). Sign up: ${link}`
        : "";

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
            toast.success("Link copied — share it anywhere ✨");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Couldn't copy — please copy manually");
        }
    };

    const copyMessage = async () => {
        try {
            await navigator.clipboard.writeText(shareMessage);
            toast.success("Message copied — paste it in a text or DM 💌");
        } catch {
            toast.error("Couldn't copy — please copy manually");
        }
    };

    const smsShare = () => {
        window.location.href = `sms:?&body=${encodeURIComponent(shareMessage)}`;
    };

    const share = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: "24K Glow On The Go",
                    text: shareMessage,
                    url: link,
                });
            } catch (err) {
                // User cancelled share or share API unavailable — fall back to copy
                console.debug("share cancelled or unsupported:", err?.name || err);
            }
        } else {
            copy();
        }
    };

    return (
        <div className="max-w-[1100px] mx-auto space-y-8">
            <div>
                <p className="text-xs tracking-[0.3em] uppercase text-rose">Friend Pass · Refer & earn</p>
                <h1 className="font-serif text-4xl sm:text-5xl mt-2">First set on us. Both of you.</h1>
                <p className="text-white/60 mt-3 max-w-2xl">
                    Share your Friend Pass. When your friend subscribes, <span className="text-gold font-medium">their first Glow set is FREE</span> (${refereeReward} credit) — and you get <span className="text-rose font-medium">${referrerReward}</span> credit toward your next month.
                </p>
            </div>

            {loading ? (
                <div className="rounded-3xl border border-white/10 bg-surface p-10 animate-pulse h-48" />
            ) : (
                <>
                    {/* Stats */}
                    <div className="grid md:grid-cols-3 gap-4">
                        <div data-testid="ref-stat-friends" className="rounded-2xl border border-white/10 bg-surface p-6 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-rose/10 border border-rose/30 flex items-center justify-center shrink-0">
                                <Users className="w-5 h-5 text-rose" />
                            </div>
                            <div>
                                <p className="text-xs tracking-widest uppercase text-white/50">Friends joined</p>
                                <p className="font-serif text-3xl mt-1">{data?.friends_referred ?? 0}</p>
                            </div>
                        </div>
                        <div data-testid="ref-stat-rewards" className="rounded-2xl border border-white/10 bg-surface p-6 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-gold/10 border border-gold/30 flex items-center justify-center shrink-0">
                                <Gift className="w-5 h-5 text-gold" />
                            </div>
                            <div>
                                <p className="text-xs tracking-widest uppercase text-white/50">Passes redeemed</p>
                                <p className="font-serif text-3xl mt-1">{data?.rewards_redeemed ?? 0}</p>
                            </div>
                        </div>
                        <div data-testid="ref-stat-credit" className="rounded-2xl border border-gold/40 bg-gradient-to-br from-gold/10 via-rose/5 to-transparent p-6 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center shrink-0">
                                <Wallet className="w-5 h-5 text-gold" />
                            </div>
                            <div>
                                <p className="text-xs tracking-widest uppercase text-gold">Account credit</p>
                                <p className="font-serif text-3xl mt-1 text-gold">${credit}</p>
                                <p className="text-[11px] text-white/50 mt-0.5">Auto-applied to your next charge</p>
                            </div>
                        </div>
                    </div>

                    {/* Code + share */}
                    <div className="rounded-3xl border border-white/10 bg-surface p-8">
                        <p className="text-xs tracking-[0.3em] uppercase text-rose">Your Friend Pass code</p>
                        <div className="mt-3 flex flex-col md:flex-row items-stretch md:items-center gap-4">
                            <div className="flex-1 px-5 py-4 rounded-2xl border border-rose/30 bg-rose/5 font-serif text-3xl tracking-wider text-rose break-all">
                                {data?.code}
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                <button
                                    data-testid="ref-copy-btn"
                                    onClick={copy}
                                    className="px-5 py-3 rounded-full bg-rose text-ink hover:bg-roseHover transition font-medium flex items-center gap-2"
                                >
                                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                    {copied ? "Copied" : "Copy link"}
                                </button>
                                <button
                                    data-testid="ref-share-btn"
                                    onClick={share}
                                    className="px-5 py-3 rounded-full border border-rose text-rose hover:bg-rose hover:text-ink transition flex items-center gap-2"
                                >
                                    <Share2 className="w-4 h-4" /> Share
                                </button>
                            </div>
                        </div>
                        <p className="mt-4 text-sm text-white/60 break-all">
                            Share link: <span className="text-white/80">{link}</span>
                        </p>

                        {/* Pre-written text template */}
                        <div className="mt-8 rounded-2xl border border-gold/25 bg-gradient-to-br from-gold/5 via-rose/5 to-transparent p-6">
                            <div className="flex items-center gap-2 mb-3">
                                <MessageSquare className="w-4 h-4 text-gold" />
                                <p className="text-xs tracking-[0.3em] uppercase text-gold">Ready-to-send message</p>
                            </div>
                            <p data-testid="ref-share-message" className="text-sm text-white/85 leading-relaxed italic">
                                &ldquo;{shareMessage}&rdquo;
                            </p>
                            <div className="mt-4 flex gap-2 flex-wrap">
                                <button
                                    data-testid="ref-sms-btn"
                                    onClick={smsShare}
                                    className="px-4 py-2 rounded-full bg-gold text-ink hover:opacity-90 transition text-sm font-medium flex items-center gap-2"
                                >
                                    <MessageSquare className="w-4 h-4" /> Text a friend
                                </button>
                                <button
                                    data-testid="ref-copy-msg-btn"
                                    onClick={copyMessage}
                                    className="px-4 py-2 rounded-full border border-white/20 text-white/85 hover:border-gold hover:text-gold transition text-sm flex items-center gap-2"
                                >
                                    <Copy className="w-4 h-4" /> Copy message
                                </button>
                            </div>
                        </div>

                        {/* How it works */}
                        <div className="mt-8 grid md:grid-cols-3 gap-3 text-sm">
                            {[
                                { n: "1", t: "Share your Friend Pass", b: "Text your code or link to a friend who'd love the glow." },
                                { n: "2", t: "They subscribe", b: `Their first Glow set is FREE — $${refereeReward} credit auto-applied.` },
                                { n: "3", t: `You earn $${referrerReward}`, b: "Credit lands on your account for the next billing cycle." },
                            ].map((s) => (
                                <div key={s.n} className="rounded-2xl border border-white/8 p-5">
                                    <p className="font-serif text-2xl text-rose">0{s.n}</p>
                                    <p className="font-serif text-lg mt-2">{s.t}</p>
                                    <p className="text-white/55 mt-1">{s.b}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Inactive callout */}
                    {sub?.status !== "active" && (
                        <Link
                            to="/portal/subscription"
                            className="block rounded-2xl border border-rose/30 bg-rose/5 p-5 hover:bg-rose/10 transition"
                        >
                            <div className="flex items-center gap-3">
                                <Sparkles className="w-5 h-5 text-rose" />
                                <p className="text-sm text-white/80">
                                    Heads up — Friend Pass credit is applied to your subscription charges. Activate your plan to start using it.
                                </p>
                            </div>
                        </Link>
                    )}
                </>
            )}
        </div>
    );
}
